import { useMemo, useState } from 'react';
import { Scissors, Loader2, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { TurfStatus, getWalkbookTurfStatus } from '@signalvote/shared';
import type { CanvasserType, WalkbookListItem } from '@signalvote/shared';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';
import { useWalkbooksWithPolygons, useVolunteers } from '@/hooks/queries';
import { TURF_STATUS_DISPLAY, getPaceLabel } from './turfStatusDisplay';

type FilterOption = 'all' | TurfStatus;

const FILTER_OPTIONS: { value: FilterOption; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: TurfStatus.Unassigned, label: 'Unassigned' },
  { value: TurfStatus.Assigned, label: 'Assigned' },
  { value: TurfStatus.InProgress, label: 'In Progress' },
  { value: TurfStatus.Completed, label: 'Completed' },
];

interface TurfListPanelProps {
  onCutTurf: () => void;
  onTurfClick: (turfId: string) => void;
}

export function TurfListPanel({ onCutTurf, onTurfClick }: TurfListPanelProps) {
  const [activeFilter, setActiveFilter] = useState<FilterOption>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedTurfId, turfListExpanded: expanded, setTurfListExpanded: setExpanded } = useFieldOpsStore();
  const { data: walkbooks = [], isLoading } = useWalkbooksWithPolygons();
  const { data: allVolunteers = [] } = useVolunteers();

  // Lookup: volunteer ID → vendor/type info for display on turf cards
  const volunteerLookup = useMemo(() => {
    const map = new Map<string, { canvasserType: CanvasserType | null; vendorName: string | null }>();
    for (const v of allVolunteers) {
      map.set(v.userId, { canvasserType: v.canvasserType, vendorName: v.vendorName });
    }
    return map;
  }, [allVolunteers]);

  const filteredWalkbooks = useMemo(() => {
    let result = walkbooks;
    if (activeFilter !== 'all') {
      result = result.filter((wb) => getWalkbookTurfStatus(wb, wb.currentShiftStatus) === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((wb) => wb.name.toLowerCase().includes(q));
    }
    return result;
  }, [walkbooks, activeFilter, searchQuery]);

  const summaryStats = useMemo(() => {
    return {
      totalTurfs: walkbooks.length,
      totalHouses: walkbooks.reduce((sum, t) => sum + t.houseCount, 0),
      totalVoters: walkbooks.reduce((sum, t) => sum + t.voterCount, 0),
    };
  }, [walkbooks]);

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden',
        'transition-all duration-300 ease-in-out',
      )}
    >
      {/* Collapsed summary bar */}
      {!expanded && (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-4 py-3"
          onClick={() => setExpanded(true)}
        >
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            <span className="font-semibold text-gray-900">Field Ops</span>
            <span className="text-gray-400">&middot;</span>
            <span className="whitespace-nowrap text-gray-500">{summaryStats.totalTurfs} turfs</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              role="button"
              tabIndex={0}
              data-coachmark="draw-turf"
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1',
                'bg-gray-900 text-xs font-medium text-white',
                'transition-colors hover:bg-gray-700'
              )}
              onClick={(e) => {
                e.stopPropagation();
                onCutTurf();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onCutTurf();
                }
              }}
            >
              <Scissors className="h-3 w-3" />
              Cut Turf
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
          </div>
        </button>
      )}

      {/* Expanded state */}
      {expanded && (
        <>
          {/* Header */}
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Field Operations</h2>
              <button
                type="button"
                className="rounded-full p-1 transition-colors hover:bg-gray-100"
                onClick={() => setExpanded(false)}
              >
                <ChevronUp className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            <div className="mt-1.5 flex gap-4 text-xs text-gray-500">
              <span>
                <span className="font-medium text-gray-900">{summaryStats.totalTurfs}</span> turfs
              </span>
              <span>
                <span className="font-medium text-gray-900">{summaryStats.totalHouses}</span> houses
              </span>
              <span>
                <span className="font-medium text-gray-900">{summaryStats.totalVoters}</span> voters
              </span>
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-1.5 px-4 pb-3">
            {FILTER_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-all duration-200',
                  activeFilter === value
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
                onClick={() => setActiveFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative px-4 pb-2">
            <Search className="pointer-events-none absolute left-7 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search turfs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-8 text-xs text-gray-700 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-7 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Turf list */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-6">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : filteredWalkbooks.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                No turfs match the selected filter.
              </div>
            ) : (
              filteredWalkbooks.map((wb) => (
                <TurfRow
                  key={wb.id}
                  walkbook={wb}
                  isSelected={wb.id === selectedTurfId}
                  status={getWalkbookTurfStatus(wb, wb.currentShiftStatus)}
                  onSelect={() => onTurfClick(wb.id)}
                  volunteerLookup={volunteerLookup}
                />
              ))
            )}
          </div>

          {/* Bottom action */}
          <div className="shrink-0 px-4 pt-2 pb-4">
            <Button
              data-coachmark="draw-turf"
              className="w-full rounded-xl"
              onClick={onCutTurf}
            >
              <Scissors className="h-4 w-4" />
              Cut New Turf
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

interface TurfRowProps {
  walkbook: WalkbookListItem;
  isSelected: boolean;
  status: TurfStatus;
  onSelect: () => void;
  volunteerLookup: Map<string, { canvasserType: CanvasserType | null; vendorName: string | null }>;
}

function TurfRow({ walkbook, isSelected, status, onSelect, volunteerLookup }: TurfRowProps) {
  const progress = walkbook.progress;
  const assignee = walkbook.assignedVolunteers[0];
  const assigneeInfo = assignee ? volunteerLookup.get(assignee.id) : undefined;

  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer flex-col gap-1.5 px-4 py-3 text-left',
        'transition-all duration-200 hover:bg-gray-50',
        isSelected && 'border-l-2 border-l-primary bg-gray-50',
        !isSelected && 'border-l-2 border-l-transparent'
      )}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: status dot + name */}
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span
            className={cn('h-2 w-2 shrink-0 rounded-full', TURF_STATUS_DISPLAY[status].bg)}
          />
          <p className="truncate text-sm font-semibold text-gray-900">{walkbook.name}</p>
        </div>

        {/* Right: house count */}
        <span className="shrink-0 text-xs text-gray-500">
          {walkbook.houseCount} houses
        </span>
      </div>

      {/* Progress bar (always shown, thin 2px) */}
      <div className="space-y-0.5">
        <div className="h-[2px] w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-200',
              TURF_STATUS_DISPLAY[status].bg,
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
        {(() => {
          const pace = getPaceLabel(progress, status);
          return pace ? (
            <p className={cn('text-[10px] font-medium', pace.className)}>
              {progress}% &mdash; {pace.label}
            </p>
          ) : progress > 0 ? (
            <p className="text-[10px] text-gray-400">{progress}%</p>
          ) : null;
        })()}
      </div>

      {/* Assignee vendor/type label */}
      {assignee && assigneeInfo && (
        <p className="text-xs text-gray-500">
          {assigneeInfo.vendorName || assigneeInfo.canvasserType || assignee.name}
        </p>
      )}
    </button>
  );
}
